import React, { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextField } from 'edenred-ui';

import {
  createRules,
  FormFields,
  useOnBlurTrim,
  useAsyncValidatorController
} from '@epi-forms/helpers';
import * as Validators from '@epi-helpers/formHelpers/validators';
import { OperatorFieldProps } from '@epi-models/forms/Controls';

import { useEInvoiceContext } from '../useEInvoiceContext';

export function OperatorField({
  readOnly,
  required = true,
  disabled = false
}: Readonly<OperatorFieldProps>) {
  const name = FormFields.Operator;

  const {
    control,
    formState: { errors },
    getValues,
    trigger
  } = useFormContext();
  const { t } = useTranslation();
  const validators = required
    ? [Validators.required, Validators.maxLength(50)]
    : [Validators.maxLength(50)];
  const rules = createRules(validators);
  const onBlurTrim = useOnBlurTrim(name);

  const {
    field: { ref, value, onChange: setValue },
    fieldState: { error }
  } = useController({
    name,
    control,
    rules
  });

  const { validateEInvoice } = useEInvoiceContext();

  const { isError, inputProps, setAsyncUnverified, clearAsyncUnverified } =
    useAsyncValidatorController({
      value,
      error,
      name: FormFields.Operator,
      isStatusSuccess: isDefaultSuccess =>
        isDefaultSuccess &&
        getValues(FormFields.EInvoiceAddress) &&
        !errors[FormFields.EInvoiceAddress]
    });

  const onChange = event => {
    setAsyncUnverified();
    setValue(event);
  };

  const onBlur = async () => {
    onBlurTrim();
    await validateEInvoice();
    clearAsyncUnverified();
  };

  useEffect(() => {
    if (!readOnly) {
      trigger(name);
    }
  }, [required]);

  return (
    <TextField
      label={t('controls.operator_label')}
      required={required}
      readOnly={readOnly}
      value={value || ''}
      disabled={disabled}
      fullWidth
      id={name}
      error={isError}
      InputProps={readOnly ? undefined : inputProps}
      helperText={error?.message}
      inputRef={ref}
      onBlur={onBlur}
      onChange={onChange}
    />
  );
}
